import { useNavigate } from 'react-router-dom';
import { CheckCircle2, CircleAlert, Database } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ViewRouter } from '@/components/views/ViewRouter';
import { navigateToFlowComposerPrompt } from '@/lib/flow-composer-handoff';
import { cn } from '@/lib/utils';
import type { DashboardChatAdHocResult, DashboardChatResult } from '@shared/types/dashboard-chat';
import type { SourcePlanFamily } from '@shared/types/capabilities';

const getSourcePlanLabel = (family: SourcePlanFamily | undefined): string | null => {
  switch (family) {
    case 'crm_query':
      return 'CRM';
    case 'flow_snapshot_read':
      return 'Run';
    case 'computed_projection':
      return 'Calcul';
    case 'static_view':
      return 'Réponse';
    case 'flow_run':
      return 'Flow';
    case 'web_research':
      return 'Recherche internet';
    case undefined:
      return null;
  }
};

const getAdHocStatusMeta = (result: DashboardChatAdHocResult) => {
  if (result.resolutionPlan.canExecuteNow === false) {
    return {
      label: 'Non disponible',
      icon: CircleAlert,
      className: 'text-amber-800',
    };
  }

  if (result.viewEnvelope) {
    return {
      label: 'Résultat prêt',
      icon: CheckCircle2,
      className: 'text-emerald-800',
    };
  }

  return {
    label: 'Réponse',
    icon: CheckCircle2,
    className: 'text-domino-inkSoft',
  };
};

export const AppChatAdHocResultCard = ({
  result,
  error: resultError,
}: {
  result: DashboardChatAdHocResult;
  error: string | null;
}) => {
  const sourceLabel = getSourcePlanLabel(result.resolutionPlan.sourcePlan?.family);
  const statusMeta = getAdHocStatusMeta(result);
  const StatusIcon = statusMeta.icon;

  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-domino-cardBorder px-3 py-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1 text-[11px] font-medium',
              statusMeta.className
            )}
          >
            <StatusIcon className="h-3.5 w-3.5" />
            {statusMeta.label}
          </span>
          {sourceLabel && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-domino-inkSoft">
              <Database className="h-3.5 w-3.5" />
              {sourceLabel}
            </span>
          )}
        </div>

      </div>

      {result.viewEnvelope ? (
        <div className="max-h-[460px] overflow-auto px-3 py-3">
          <ViewRouter
            viewDefinition={result.viewDefinition ?? null}
            payload={result.viewEnvelope}
            fallbackReason="ad_hoc_view_payload_invalid"
          />
        </div>
      ) : (
        <p className="px-3 py-3 text-xs leading-5 text-domino-inkSoft">
          {result.resolutionPlan.explanation}
        </p>
      )}

      {resultError && (
        <p className="border-t border-red-100 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">
          {resultError}
        </p>
      )}
    </div>
  );
};


/** Opens what a handoff result points to (Forge, a tool page, a template, a page). */
export const useRunHandoffAction = () => {
  const navigate = useNavigate();
  return (result: DashboardChatResult) => {
    switch (result.type) {
      case 'flow_handoff':
        navigateToFlowComposerPrompt(navigate, result.seed.prompt);
        return;
      case 'prebuilt_tool_handoff':
        navigate(`/tools/${encodeURIComponent(result.toolKey)}`);
        return;
      case 'existing_flow':
        navigate(`/tools/${encodeURIComponent(result.flowId)}`);
        return;
      case 'template_handoff':
        if (result.templateKey === 'post_call_actions') {
          navigate('/tools/analyze_call');
          return;
        }
        navigate(
          `/templates?template=${encodeURIComponent(result.templateKey)}&source=app_chat`
        );
        return;
      case 'navigation_handoff':
        if (result.href.startsWith('/') && !result.href.startsWith('//')) {
          navigate(result.href);
          return;
        }
        window.location.assign(result.href);
        return;
      default:
        return;
    }
  };
};

const getHandoffCard = (result: DashboardChatResult) => {
  switch (result.type) {
    case 'flow_handoff':
      return {
        title: 'Créer un outil Domino',
        body: 'Domino peut ouvrir Forge avec cette demande préremplie. Rien n’est créé tant que tu ne valides pas.',
        actionLabel: 'Créer l’outil',
      };
    case 'prebuilt_tool_handoff':
      return {
        title: result.toolTitle,
        body: result.answer || 'Ouvre la page outil dédiée pour continuer.',
        actionLabel: `Ouvrir ${result.toolTitle}`,
      };
    case 'existing_flow':
      return {
        title: result.flowTitle,
        body: result.answer || 'Une capacité existante correspond à cette demande.',
        actionLabel: 'Ouvrir le flow',
      };
    case 'template_handoff':
      return {
        title:
          result.templateKey === 'post_call_actions'
            ? 'Analyser un appel'
            : result.templateTitle,
        body:
          result.templateKey === 'post_call_actions'
            ? 'Cette suite passe par la page outil dédiée pour analyser le call et ses actions.'
            : result.answer,
        actionLabel:
          result.templateKey === 'post_call_actions'
            ? 'Ouvrir Analyser un appel'
            : 'Ouvrir le template',
      };
    case 'navigation_handoff':
      return {
        title: result.title,
        body: result.answer,
        actionLabel: result.actionLabel,
      };
    default:
      return null;
  }
};


export const AppChatHandoffCard = ({
  result,
  onRun,
}: {
  result: DashboardChatResult;
  onRun: () => void;
}) => {
  const handoff = getHandoffCard(result);
  if (!handoff) return null;

  return (
    <div className="mt-3 rounded-2xl border border-domino-cardBorder bg-domino-white px-3 py-3 shadow-sm">
      <p className="text-[11px] font-medium text-domino-aubergine">Suite recommandée</p>
      <p className="mt-3 text-sm font-semibold text-domino-ink">{handoff.title}</p>
      <p className="mt-2 text-xs leading-5 text-domino-inkSoft">{handoff.body}</p>
      <Button size="sm" variant="secondary" className="mt-3" onClick={onRun}>
        {handoff.actionLabel}
      </Button>
    </div>
  );
};
